import { useRef, useState } from 'react';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { KnowledgeScope } from './scopeMeta';

const ACCEPT =
  '.pdf,.docx,.txt,.md,.markdown,.csv,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface KnowledgeUploadDropzoneProps {
  scope: KnowledgeScope;
  /** Scope color, drives the drag-over highlight so the target scope is obvious. */
  accentColor: string;
  uploading?: boolean;
  error?: string | null;
  onUpload: (file: File) => Promise<unknown>;
}

/** rgba from a #rrggbb hex at a given alpha. */
function tint(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return `rgba(0, 82, 255, ${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

export default function KnowledgeUploadDropzone({
  scope,
  accentColor,
  uploading = false,
  error,
  onUpload,
}: KnowledgeUploadDropzoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    for (let i = 0; i < files.length; i += 1) {
      await onUpload(files[i]);
    }
  };

  return (
    <div className="space-y-1.5">
      <div
        role="button"
        tabIndex={0}
        aria-label={t('knowledge.dropAria', {
          defaultValue: 'อัปโหลดเอกสาร ({{scope}})',
          scope,
        })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className="motion-safe:transition-all motion-safe:duration-150 cursor-pointer rounded-md border border-dashed px-3 py-2 flex flex-wrap items-center justify-center gap-2 focus:outline-none focus-visible:ring-2"
        style={{
          borderColor: dragOver ? accentColor : tint(accentColor, 0.4),
          background: dragOver ? tint(accentColor, 0.1) : 'transparent',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={ACCEPT}
          multiple
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <ArrowUpTrayIcon
          className={`h-4 w-4 shrink-0 ${uploading ? 'motion-safe:animate-bounce' : ''}`}
          style={{ color: dragOver ? accentColor : 'var(--color-app-text-dim, #7c828a)' }}
        />
        <div className="flex flex-wrap items-baseline gap-1.5">
          <p className="text-xs font-medium text-app-text">
            {uploading
              ? t('knowledge.uploading', { defaultValue: 'กำลังอัปโหลด…' })
              : t('knowledge.dropCtaCompact', {
                  defaultValue: 'ลากไฟล์มาวาง หรือคลิกเพื่ออัปโหลด',
                })}
          </p>
          <p className="hidden text-[10px] text-app-text-dim sm:inline-block">
            {t('knowledge.dropTypesCompact', {
              defaultValue: '(PDF, DOCX, TXT, MD, CSV, Excel)',
            })}
          </p>
        </div>
      </div>
      {error ? (
        <p className="rounded-md bg-semantic-down/10 px-2 py-1 text-xs text-semantic-down">
          {error}
        </p>
      ) : null}
    </div>
  );
}
